import { useEffect, useMemo, useState } from "react";
import { ApiRequestError, runSoql } from "../lib/client";
import {
  SOQL_DEFAULT_ROW_LIMIT,
  SOQL_MAX_ROW_LIMIT,
  type SoqlResponse,
} from "../lib/types";

const DEFAULT_QUERY = "SELECT Id, Name, StageName, Amount FROM Opportunity";

// Switching tabs unmounts this console, so a half-written query would be lost.
// Keep the inputs in localStorage instead of retyping them.
const STORAGE_KEY_QUERY = "ic.soqlQuery";
const STORAGE_KEY_ROW_LIMIT = "ic.soqlRowLimit";

/** Read-only SOQL console for diagnosing a customer's data. SELECT-only. */
export function SoqlConsole({
  subscriberId,
  subscriberName,
}: {
  subscriberId: string;
  subscriberName: string;
}) {
  const [query, setQuery] = useState(
    () => localStorage.getItem(STORAGE_KEY_QUERY) ?? DEFAULT_QUERY,
  );
  const [rowLimit, setRowLimit] = useState(() => {
    const saved = Number(localStorage.getItem(STORAGE_KEY_ROW_LIMIT));
    return saved >= 1 && saved <= SOQL_MAX_ROW_LIMIT
      ? saved
      : SOQL_DEFAULT_ROW_LIMIT;
  });
  const [result, setResult] = useState<SoqlResponse | null>(null);
  const [queryDt, setQueryDt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_QUERY, query);
  }, [query]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_ROW_LIMIT, String(rowLimit));
  }, [rowLimit]);

  // Mirror the server's read-only guard so we can warn before sending.
  const clientWarning = useMemo(() => {
    const q = query.trim();
    if (!q) return null;
    if (!/^select\b/i.test(q)) return "Query must start with SELECT.";
    if (q.includes(";")) return "Query must not contain ';'.";
    return null;
  }, [query]);

  async function run() {
    const q = query.trim();
    if (!q || clientWarning) return;
    setLoading(true);
    setError(null);
    try {
      const data = await runSoql(subscriberId, {
        query: q,
        row_limit: rowLimit,
      });
      setResult(data);
      setQueryDt(new Date());
    } catch (err) {
      setResult(null);
      setError(err instanceof ApiRequestError ? err.message : "Query failed.");
    } finally {
      setLoading(false);
    }
  }

  const columns = useMemo(() => {
    if (!result?.records.length) return [];
    const seen = new Set<string>();
    for (const rec of result.records)
      for (const k of Object.keys(rec)) seen.add(k);
    return [...seen];
  }, [result]);

  return (
    <section className="panel">
      <textarea
        className="soql-input"
        spellCheck={false}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") run();
        }}
        placeholder="SELECT Id, Name FROM Account WHERE ..."
      />
      <div className="toolbar">
        <label className="check">
          row_limit
          <input
            type="number"
            className="num-input"
            min={1}
            max={SOQL_MAX_ROW_LIMIT}
            value={rowLimit}
            onChange={(e) =>
              setRowLimit(
                Math.min(
                  SOQL_MAX_ROW_LIMIT,
                  Math.max(1, Number(e.target.value) || SOQL_DEFAULT_ROW_LIMIT),
                ),
              )
            }
          />
        </label>
        <span className="hint">read-only · ⌘/Ctrl+Enter to run</span>
        <button
          type="button"
          className="btn primary"
          onClick={run}
          disabled={loading || !!clientWarning}
        >
          {loading ? "Running…" : "Run query"}
        </button>
      </div>

      {clientWarning && <div className="banner warn">{clientWarning}</div>}
      {error && <div className="banner error">{error}</div>}

      {result && (
        <>
          <div className="toolbar">
            <span className="count">{result.count} record(s)</span>
          </div>
          {result.records.length === 0 ? (
            <div className="placeholder">No records.</div>
          ) : (
            <div>
              <div className="toolbar">
                <button
                  type="button"
                  className="btn primary"
                  onClick={() =>
                    downloadCsv(
                      columns,
                      result.records,
                      `soql-${subscriberName}-${formatDatetime(queryDt)}.csv`,
                    )
                  }
                >
                  Download CSV
                </button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      {columns.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.records.map((rec, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: SOQL records have no stable id
                      <tr key={i}>
                        {columns.map((c) => (
                          <td key={c}>
                            <Cell value={rec[c]} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Serialize one SOQL value the way a spreadsheet expects it. */
function csvValue(value: unknown) {
  if (value === null || value === undefined) return "";
  const raw = typeof value === "object" ? JSON.stringify(value) : String(value);
  // Neutralize spreadsheet formula injection (=, +, -, @, tab, CR) without
  // turning plain numbers like -500 into text.
  const risky = /^[=+\-@\t\r]/.test(raw) && !/^-?\d+(\.\d+)?$/.test(raw);
  const text = risky ? `'${raw}` : raw;
  // Quote anything that could break a row, doubling embedded quotes.
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/** Build a CSV document from the query's columns and records. */
export function recordsToCsv(
  columns: string[],
  records: Array<Record<string, unknown>>,
) {
  const rows = [
    columns.map(csvValue).join(","),
    ...records.map((rec) => columns.map((c) => csvValue(rec[c])).join(",")),
  ];
  return `${rows.join("\r\n")}\r\n`;
}

/** Trigger a browser download of the SOQL results as a CSV file. */
function downloadCsv(
  columns: string[],
  records: Array<Record<string, unknown>>,
  filename: string,
) {
  // Leading BOM so Excel reads the file as UTF-8.
  const blob = new Blob([`﻿${recordsToCsv(columns, records)}`], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function formatDatetime(date: Date | null): string {
  if (date === null) {
    return ``;
  }
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/** Render a SOQL cell; nested objects/arrays (subqueries) become compact JSON. */
function Cell({ value }: { value: unknown }) {
  if (value === null || value === undefined)
    return <span className="muted">∅</span>;
  if (typeof value === "object")
    return <code className="nested">{JSON.stringify(value)}</code>;
  return <>{String(value)}</>;
}
